import { DataTable } from '@/components/ui/data-table'
import { MultiSelect } from '@/components/ui/multi-select'
import { Skeleton } from '@/components/ui/skeleton'
import { ColumnDef } from '@tanstack/react-table'
import Section from 'app/frontend/base/components/Section'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { Children, ReactElement, useState } from 'react'
import { AuditContext, useAudit } from '../TeamAudit/context'
import AuditFilter from '../TeamAudit/components/AuditFilter'
import { useEventsInsightsColumns } from '../TeamAudit/column-definitions/events_insights'

const TeamEventInsightsContent = () => {
  const { team } = useTeam()
  const { visibleTeamRanks, setVisibleTeamRanks } = useAudit()
  const { columns, data, filters, initialSortBy, emptyMessage, loading } =
    useEventsInsightsColumns()
  const allFilters = Children.toArray(filters ?? []) as ReactElement[]
  const headerFilters = allFilters.filter((filter) => {
    const key = filter.key?.toString() ?? ''
    return key.includes('dateRange') || key.includes('season')
  })
  const contentFilters = allFilters.filter((filter) => {
    const key = filter.key?.toString() ?? ''
    return !key.includes('dateRange') && !key.includes('season')
  })

  return (
    <>
      <h2 className='flex justify-between items-center gap-4 flex-wrap'>
        <div className='flex items-center whitespace-nowrap'>
          Event insights
        </div>
        <div className='flex items-center gap-2 flex-wrap'>
          {headerFilters.map((filter) => (
            <div key={filter.key?.toString()}>{filter.props.children}</div>
          ))}
        </div>
      </h2>
      <div className='mt-6 flex gap-2 justify-between items-end'>
        <div className='flex gap-2 flex-wrap'>
          <AuditFilter label='Characters to show' key='team-ranks'>
            <MultiSelect<string>
              options={(team.teamRanks ?? []).map((rank) => ({
                value: rank.id.toString(),
                label: (
                  <span style={{ color: rank.color ?? undefined }}>
                    {rank.name}
                  </span>
                ),
              }))}
              value={visibleTeamRanks}
              placeholder='All ranks'
              onChange={setVisibleTeamRanks}
            />
          </AuditFilter>
          {contentFilters}
        </div>
      </div>
      {loading ? (
        <Skeleton className='mt-2 h-160 w-full' />
      ) : (
        <DataTable<unknown, unknown>
          columns={columns as ColumnDef<unknown>[]}
          data={data as unknown[]}
          headerClassName={(i) => (i === 0 ? 'text-left' : 'text-center')}
          rowClassName={() => 'text-center'}
          className='mt-2'
          includeHeader={true}
          sortingState={initialSortBy ?? []}
          emptyMessage={() =>
            emptyMessage ?? 'No event insights found for the selected filters.'
          }
          stickyFirstColumn
        />
      )}
    </>
  )
}

const TeamEventInsights = () => {
  const { team, guild } = useTeam()
  const {
    data: { currentSeason },
  } = usePageInfo()
  const [visibleTeamRanks, setVisibleTeamRanks] = useState<string[]>([])

  return (
    <Section title={`Event Insights | ${team.name} | ${guild.name}`}>
      <AuditContext.Provider
        value={{
          loadingAuditData: false,
          selectedSeason: currentSeason,
          setSelectedSeason: () => null,
          visibleMembers: [],
          weeksAgo: 0,
          setWeeksAgo: () => null,
          showingRanks: false,
          setShowingRanks: () => null,
          activeView: 'list',
          setActiveView: () => null,
          setRefreshStatusOpen: () => null,
          visibleTeamRanks,
          setVisibleTeamRanks,
        }}
      >
        <TeamEventInsightsContent />
      </AuditContext.Provider>
    </Section>
  )
}

export default TeamEventInsights
